import type { components, paths } from "./schema";

// A small typed client over the generated OpenAPI schema.
//
// It exists instead of a generated client because the interesting behaviour is
// not the request shapes — those come from schema.d.ts — but what every request
// has in common: the bearer token, the active tenant, and what to do when the
// server says the session is over.

export const API_BASE = "/api/v1";

type JSON200<P extends keyof paths, M extends keyof paths[P]> = paths[P][M] extends {
  responses: { 200: { content: { "application/json": infer R } } };
}
  ? R
  : never;


/** Where the session lives between page loads. */
const STORAGE_KEY = "heimdall.session";

export interface Session {
 labOnly?: boolean;
  /**
   * Only for a session established by an API token the person pasted in.
   *
   * A password login leaves this empty: the credential is an HttpOnly cookie
   * the page cannot read, which is the point (§12.1). What stays here is the
   * bookkeeping a console needs — which tenant is active, what to show in the
   * corner — none of which is a credential.
   */
  token?: string;
  /**
   * The active tenant. A session token from a password login is not bound to a
   * tenant — identity spans tenants while a role is always per-tenant — so it
   * has to be selected per request. An API token carries its own binding and
   * ignores this, but sending it is harmless as long as it agrees.
   */
  tenantId: string;
  /** For display only; the server is the authority on what the caller may do. */
  email?: string;
  /** The active tenant's human name, so the switcher can read as something. */
  tenantName?: string;
  /** The role held in the active tenant, for display. */
  role?: string;
  /**
   * Platform (cross-tenant) standing in the active tenant. For deciding what to
   * offer, never for deciding what is allowed — the server re-checks it on
   * every platform request, and a session that claimed it falsely would simply
   * be refused.
   */
  platform?: boolean;
}

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function saveSession(s: Session): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** Thrown for any non-2xx response, carrying the status so callers can branch. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
  /** The session is gone or was never valid. */
  get isAuth(): boolean {
    return this.status === 401;
  }
  /** Authenticated, but not allowed to do this. */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  onResponse?: (response: Response) => void;
  method?: Method;
  body?: unknown;
  /** Omit the tenant selector — only /auth/login, which has no session yet. */
  anonymous?: boolean;
  /** The operator's reason for a cross-tenant write, recorded in the audit log (§3.3). */
  escalate?: string;
  signal?: AbortSignal;
}

/**
 * onAuthLost is called when the server rejects the session, so the shell can
 * send the operator back to sign-in from one place rather than every caller
 * remembering to.
 */
let onAuthLost: (() => void) | null = null;
export function setAuthLostHandler(fn: () => void): void {
  onAuthLost = fn;
}

/**
 * The CSRF token the server set beside the session cookie (§12.1).
 *
 * The session cookie is HttpOnly and this one is not, on purpose: the page has
 * to echo this back in a header, and being able to read it is what proves the
 * request came from this origin. A cross-site page can cause the *cookie* to be
 * sent — that is what cookies do — but cannot read it.
 */
function csrfToken(): string {
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === "__Host-heimdall_csrf" || name === "heimdall_csrf") {
      return decodeURIComponent(rest.join("="));
    }
  }
  return "";
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const session = loadSession();
  const headers: Record<string, string> = {};

  if (!opts.anonymous) {
    if (!session) {
      onAuthLost?.();
      throw new ApiError(401, "not signed in");
    }
    // A password login authenticates by cookie and keeps no token here — the
    // whole point of the change. An API token pasted into the sign-in form is
    // still sent as a header: it has no cookie, and it is a credential the
    // person chose to hold.
    if (session.token) headers["Authorization"] = `Bearer ${session.token}`;
    if (session.tenantId) headers["X-Heimdall-Tenant"] = session.tenantId;
  }
  const method = opts.method ?? "GET";
  if (method !== "GET") {
    const csrf = csrfToken();
    if (csrf) headers["X-Heimdall-CSRF"] = csrf;
  }
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  // A cross-tenant write carries the operator's reason, which the server
  // records in the audit entry (§3.3). Typed by the person doing it, not
  // generated here: a reason the console invented would be worse than none —
  // it would look like an answer in the log and be nobody's.
  if (opts.escalate) headers["X-Heimdall-Escalate"] = opts.escalate;

  const res = await fetch(API_BASE + path, {
    method,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    // The session cookie has to go with it. Same-origin is the default in
    // modern browsers, but the console is served from the API's own origin and
    // saying so is cheaper than depending on a default.
    credentials: "same-origin",
  });

  if (res.status === 401 && !opts.anonymous) onAuthLost?.();

  if (!res.ok) {
    throw new ApiError(res.status, await errorMessage(res));
  }
  opts.onResponse?.(res);
  return (await successBody(res)) as T;
}

/**
 * The body of a successful response. Several calls answer 202 Accepted with no
 * body at all — apply, confirm and dry-run on a network config among them — and
 * res.json() on an empty body throws. Safari words that as "The string did not
 * match the expected pattern.", which was shown as the result of an apply that
 * had in fact been accepted.
 */
async function successBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (text.trim() === "") return undefined;
  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError(res.status, `the server answered ${res.status} with something that is not JSON: ${text.slice(0, 200)}`);
  }
}

/** The API returns {"error": "..."}; fall back to the status when it does not. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    /* not JSON */
  }
  return `${res.status} ${res.statusText}`;
}

/** Follow only same-origin API pagination; never send credentials to a Link host. */
export function nextPage(link: string | null): string | null {
  const match = link?.match(/<([^>]+)>;\s*rel="next"/);
  if (!match) return null;
  const url = new URL(match[1]!, window.location.origin);
  if (url.origin !== window.location.origin || !url.pathname.startsWith(API_BASE + "/")) {
    throw new Error("Invalid API pagination link");
  }
  return url.pathname.slice(API_BASE.length) + url.search;
}

async function getAll<T>(path: string, signal?: AbortSignal): Promise<T> {
  let next: string | null = path;
  const seen = new Set<string>();
  const rows: unknown[] = [];
  while (next) {
    if (seen.has(next)) throw new Error("API pagination repeated a page");
    seen.add(next);
    let following: string | null = null;
    const result = await request<T>(next, { signal, onResponse: (r) => { following = nextPage(r.headers.get("Link")); } });
    if (!Array.isArray(result)) return result;
    rows.push(...result);
    next = following;
  }
  return rows as T;
}

/** UI affordances only; the server remains the permission authority. */
export function canOperate(): boolean {
  return ["operator", "admin", "owner"].includes(loadSession()?.role ?? "");
}
export function canAdmin(): boolean {
  return ["admin", "owner"].includes(loadSession()?.role ?? "");
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    new URLSearchParams(path.split("?")[1]).has("limit") ? request<T>(path, { signal }) : getAll<T>(path, signal),
  post: <T>(path: string, body?: unknown, escalate?: string) =>
    request<T>(path, { method: "POST", body, escalate }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, escalate?: string) =>
    request<T>(path, { method: "PATCH", body, escalate }),
  del: <T>(path: string, escalate?: string) => request<T>(path, { method: "DELETE", escalate }),
};

// --- authentication ---------------------------------------------------------

type LoginResult = paths["/auth/login"]["post"]["responses"]["200"]["content"]["application/json"];

/**
 * What a login attempt produced. A password alone is not always an answer:
 * where a second factor is in force the reply asks for one, and where a tenant
 * requires a factor the account has not enrolled it hands back a session that
 * can do nothing but enrol (§3.3).
 */
export type LoginOutcome =
  | { kind: "ok"; token: string }
  | { kind: "totp"; message: string }
  | { kind: "enroll"; token: string };

interface LoginBody {
  email: string;
  password: string;
  totp_code?: string;
  recovery_code?: string;
}

/**
 * Exchange local credentials for a session token. Public by necessity: it is
 * where a caller gets the credential everything else requires.
 *
 * This one reads the body on failure as well as success, because a 401 here is
 * not always a refusal — "bring a code" is a step, not an error.
 */
export async function login(
  email: string,
  password: string,
  second?: { code?: string; recoveryCode?: string },
): Promise<LoginOutcome> {
  const body: LoginBody = { email, password };
  if (second?.code) body.totp_code = second.code;
  if (second?.recoveryCode) body.recovery_code = second.recoveryCode;

  const res = await fetch(API_BASE + "/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const payload = (await res.json().catch(() => ({}))) as LoginResult & {
    error?: string;
    totp_required?: boolean;
  };
  if (res.ok) {
    return payload.enrollment_required
      ? { kind: "enroll", token: payload.token }
      : { kind: "ok", token: payload.token };
  }
  if (res.status === 401 && payload.totp_required) {
    return { kind: "totp", message: payload.error ?? "A second factor is required." };
  }
  throw new ApiError(res.status, payload.error ?? `${res.status} ${res.statusText}`);
}

// --- second factor (§3.3) ---------------------------------------------------

export type TOTPStatus = JSON200<"/auth/totp", "get">;
export type TOTPEnrollment = JSON200<"/auth/totp/enroll", "post">;

export function totpStatus(): Promise<TOTPStatus> {
  return api.get<TOTPStatus>("/auth/totp");
}

export function startTOTPEnrollment(): Promise<TOTPEnrollment> {
  return api.post<TOTPEnrollment>("/auth/totp/enroll");
}

/**
 * Confirm an enrolment. When the caller was on the restricted session issued
 * because a tenant requires a factor, the reply carries a real session — the
 * enrolment it was restricted for is done.
 */
export async function confirmTOTPEnrollment(code: string): Promise<{ codes: string[]; token?: string }> {
  const out = await api.post<{ recovery_codes: string[]; token?: string }>("/auth/totp/confirm", { code });
  return { codes: out.recovery_codes, token: out.token };
}

export function disableTOTP(password: string): Promise<void> {
  return api.post("/auth/totp/disable", { password });
}

// --- agent artifacts (§7.4) -------------------------------------------------

export type AgentArtifact = JSON200<"/agent-artifacts", "get"> extends (infer A)[] ? A : never;

export function listAgentArtifacts(signal?: AbortSignal): Promise<AgentArtifact[]> {
  return api.get<AgentArtifact[]>("/agent-artifacts", signal);
}

// --- invitations (§3.3) -----------------------------------------------------

export type Invitation = JSON200<"/invitations", "get"> extends (infer I)[] ? I : never;
export type InvitationDetail = JSON200<"/auth/invitation", "get">;

export function listInvitations(signal?: AbortSignal): Promise<Invitation[]> {
  return api.get<Invitation[]>("/invitations", signal);
}

export function createInvitation(email: string, role: string): Promise<Invitation> {
  return api.post<Invitation>("/invitations", { email, role });
}

export function revokeInvitation(id: string): Promise<void> {
  return api.del(`/invitations/${id}`);
}

/**
 * What a link is for, before anyone commits to it. Anonymous by necessity: the
 * recipient has no account yet, which is what the link is about to give them.
 */
export async function invitationDetail(token: string): Promise<InvitationDetail> {
  const res = await fetch(`${API_BASE}/auth/invitation?token=${encodeURIComponent(token)}`);
  if (!res.ok) throw new ApiError(res.status, "this invitation link is not valid");
  return (await res.json()) as InvitationDetail;
}

/** Accept an invitation, which creates the account and returns a session. */
export async function acceptInvitation(token: string, password: string): Promise<string> {
  const res = await fetch(`${API_BASE}/auth/invitation/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  const body = (await res.json().catch(() => ({}))) as { token?: string; error?: string };
  if (!res.ok || !body.token) {
    throw new ApiError(res.status, body.error ?? "this invitation could not be accepted");
  }
  return body.token;
}

export type TenantSecurity = JSON200<"/tenant-security", "get">;

export function tenantSecurity(signal?: AbortSignal): Promise<TenantSecurity> {
  return api.get<TenantSecurity>("/tenant-security", signal);
}

export function setTenantSecurity(
  requireTotp: boolean,
  policy?: Partial<Omit<TenantSecurity, "require_totp">>,
): Promise<void> {
  return api.put("/tenant-security", { require_totp: requireTotp, ...(policy ?? {}) });
}

/**
 * The tenants this caller may act in. Identity-scoped, so it works before a
 * tenant has been chosen — which is the whole point, since choosing one is what
 * it enables.
 */
export type TenantAccess = JSON200<"/auth/tenants", "get"> extends (infer T)[] ? T : never;

export function listMyTenants(): Promise<TenantAccess[]> {
  return api.get<TenantAccess[]>("/auth/tenants");
}

export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout");
  } catch {
    // A session that is already gone server-side is still gone; clearing it
    // locally is what matters and must not be blocked by the round trip.
  }
  clearSession();
}

// --- convenience types ------------------------------------------------------

export type Agent = JSON200<"/agents", "get"> extends (infer E)[] ? E : never;
export type Test = JSON200<"/tests", "get"> extends (infer T)[] ? T : never;
export type EffectiveTest = JSON200<"/agents/{id}/effective-tests", "get"> extends (infer T)[] ? T : never;
export type Alert = JSON200<"/alerts", "get"> extends (infer A)[] ? A : never;
export type AlertRule = JSON200<"/alert-rules", "get"> extends (infer R)[] ? R : never;
export type Group = JSON200<"/groups", "get"> extends (infer G)[] ? G : never;
export type TenantVRF = JSON200<"/vrfs", "get"> extends (infer V)[] ? V : never;
export type Campaign = JSON200<"/campaigns", "get"> extends (infer C)[] ? C : never;
export type SavedTrafficProfile = JSON200<"/traffic-profiles", "get"> extends (infer P)[] ? P : never;
export type CampaignDetail = JSON200<"/campaigns/{id}", "get">;
export type CampaignCase = CampaignDetail["cases"][number];
export type CampaignRun = CampaignDetail["runs"][number];
export type ApiToken = JSON200<"/tokens", "get"> extends (infer T)[] ? T : never;
export type AuditEntry = JSON200<"/audit-log", "get"> extends (infer A)[] ? A : never;
export type Impairment = JSON200<"/impairments", "get"> extends (infer I)[] ? I : never;
export type MaintenanceWindow = JSON200<"/maintenance-windows", "get"> extends (infer M)[] ? M : never;
export type Secret = JSON200<"/secrets", "get"> extends (infer S)[] ? S : never;
export type ScenarioRun = JSON200<"/scenarios", "get"> extends (infer S)[] ? S : never;
export type ScenarioStep = Schema["ScenarioStep"];
export type SessionSchedule = JSON200<"/session-schedules", "get"> extends (infer S)[] ? S : never;
// GET /test-sessions (list) is served by the query router, not the generated
// admin API, so there is no JSON200 path to derive an array element from —
// but it returns the same TestSession shape as the admin API's GET-by-id.
export type TestSession = Schema["TestSession"];
export type NetworkInterface = Schema["NetworkInterface"];
/** The agent's live network configuration, as it last reported it. */
export type NetState = Schema["NetState"];
export type NetStateInterface = Schema["NetStateInterface"];
export type PlatformTenant = JSON200<"/platform/tenants", "get"> extends (infer T)[] ? T : never;
export type RolloutSummary = JSON200<"/rollouts", "get"> extends (infer R)[] ? R : never;
export type EnrollmentRequest = JSON200<"/enrollment-requests", "get"> extends (infer E)[] ? E : never;
export type DeniedMachine = JSON200<"/enrollment-denylist", "get"> extends (infer E)[] ? E : never;
export type RecentChange = JSON200<"/changes", "get"> extends (infer E)[] ? E : never;
export type RetentionHold = JSON200<"/retention-holds", "get"> extends (infer E)[] ? E : never;
export type BulkRevertResult = JSON200<"/config-versions/bulk-revert", "post">;
export type JoinToken = JSON200<"/join-tokens", "get"> extends (infer J)[] ? J : never;
export type NotificationChannel = JSON200<"/notification-channels", "get"> extends (infer C)[] ? C : never;
export type StorageStatus = JSON200<"/platform/storage", "get">;
export type ServerHealth = JSON200<"/platform/health", "get">;
export type TenantUser = JSON200<"/users", "get"> extends (infer U)[] ? U : never;

// --- enum helpers -----------------------------------------------------------
//
// Dropdown options are exactly the values the API accepts, checked at compile
// time. Written out by hand they were wrong three times in a row — interface
// roles, route-injection kinds, session kinds — because a plausible guess
// ("wan", "longer-path", "throughput") builds and renders perfectly and only
// fails when someone submits the form. `satisfies` turns each of those into a
// type error instead.
type Schema = components["schemas"];

export type InterfaceRole = Schema["InterfaceConfig"]["role"];
export type RouteInjectionKind = Schema["RouteInjectionCreate"]["kind"];
export type SessionKind = Schema["TestSessionCreate"]["kind"];
export type TestKind = Schema["TestCreate"]["kind"];
export type AlertSeverity = Schema["AlertRuleCreate"]["severity"];
export type AlertComparator = NonNullable<Schema["AlertRuleCreate"]["comparator"]>;
export type AlertMetric = NonNullable<Schema["AlertRuleCreate"]["metric"]>;
export type RoleName = Schema["UserCreate"]["role"];
export type TokenScope = NonNullable<Schema["ApiTokenCreate"]["scopes"]>[number];
export type ChannelKind = Schema["NotificationChannelCreate"]["kind"];
export type SecurityScope = Schema["SecurityScope"];
export type SecurityRun = Schema["SecurityRun"];
export type SecurityResult = Schema["SecurityResult"];
export type InspectionCA = Schema["InspectionCA"];
export type SecurityMarker = Schema["SecurityMarker"];
export type ScenarioState = ScenarioRun["state"];
